/**
 * A semaphore implementation for controlling concurrent access to a shared resource.
 * Limits the number of concurrent operations to a specified maximum.
 */
export default class Semaphore {
  /** Current number of active operations */
  private count: number

  /** Queue of pending operations waiting to acquire the semaphore */
  private queue: (() => void)[]

  /**
   * Creates a new semaphore with the specified concurrency limit
   * @param limit Maximum number of concurrent operations allowed
   * @throws Error if limit is less than 1
   */
  constructor(private limit: number) {
    if (limit < 1) {
      throw new Error('Semaphore limit must be at least 1')
    }
    this.count = 0
    this.queue = []
    this.limit = limit
  }

  /**
   * Acquires the semaphore, allowing execution to proceed if under the limit
   * or queuing the operation if at the limit
   * @returns Promise that resolves when the semaphore is acquired
   * @throws Error if semaphore is in an invalid state
   */
  async acquire(): Promise<void> {
    if (this.count < 0) {
      throw new Error('Semaphore count cannot be negative')
    }

    if (this.count < this.limit) {
      // Acquire immediately
      this.count += 1
      return Promise.resolve()
    }

    // Need to wait
    await new Promise<void>((resolve) => {
      this.queue.push(resolve) // Push the resolve function itself
    })

    // After being dequeued and resolved by release()
    if (this.count >= this.limit) {
      // Sanity check: If count is still >= limit after being resumed, something is wrong.
      throw new Error('Semaphore logic error: Resumed acquire when count still at limit.')
    }
    this.count += 1 // Increment count *after* resuming
    return Promise.resolve() // Explicitly return resolved promise for void
  }

  /**
   * Releases the semaphore, allowing the next queued operation to proceed
   */
  async release(): Promise<void> {
    if (this.count <= 0) {
      return // Return early to prevent crash
    }
    this.count -= 1

    if (this.queue.length > 0) {
      const nextResolve = this.queue.shift() // Get the resolve function
      if (nextResolve) {
        nextResolve() // Resolve the promise the next acquire is waiting for
      }
    }
  }

  /**
   * Executes a function with semaphore control
   * @param fn Function to execute
   * @throws Error if the function execution fails
   */
  async execute<T = void>(fn: () => Promise<T>): Promise<T> {
    if (!fn) {
      throw new Error('Function to execute cannot be null or undefined')
    }
    let acquired = false
    try {
      await this.acquire()
      acquired = true
      // Execute the function and return its result
      return await fn()
    } finally {
      if (acquired) {
        await this.release()
      }
    }
  }
}
